import { logger } from '../utils/logger';

interface FetchWrapperConfig {
  getAccessToken: () => string | null;
  getRefreshToken: () => string | null;
  setTokens: (tokens: { access: string; refresh: string }) => void;
  onLogout: () => void;
  refreshEndpoint: string;
}

class FetchWrapper {
  private config: FetchWrapperConfig;
  private baseURL = '/api/projectag';

  constructor(config: FetchWrapperConfig) {
    this.config = config;
  }

  private async makeRequest<T>(
    url: string,
    options: RequestInit = {}
  ): Promise<T> {
    const token = this.config.getAccessToken();
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const fullUrl = `${this.baseURL}${url}`;

    const response = await fetch(fullUrl, {
      ...options,
      headers,
    });

    if (response.status === 401) {
      // Token expired, try to refresh
      const refreshed = await this.refreshToken();
      if (refreshed) {
        // Retry the original request with new token
        const newToken = this.config.getAccessToken();
        if (newToken) {
          headers['Authorization'] = `Bearer ${newToken}`;
          const retryResponse = await fetch(fullUrl, {
            ...options,
            headers,
          });

          if (retryResponse.ok) {
            return retryResponse.json();
          }
        }
      }
      
      // If refresh failed, logout
      this.config.onLogout();
      throw new Error('Authentication failed');
    }

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  private async refreshToken(): Promise<boolean> {
    try {
      const refreshToken = this.config.getRefreshToken();
      if (!refreshToken) {
        return false;
      }

      const response = await fetch(`${this.baseURL}/${this.config.refreshEndpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          refresh_token: refreshToken,
          grant_type: 'refresh_token',
        }),
      });

      if (response.ok) {
        const data = await response.json();
        this.config.setTokens({
          access: data.access_token,
          refresh: data.refresh_token,
        });
        return true;
      }
    } catch (error) {
      logger.error('Token refresh failed:', error);
    }
    
    return false;
  }

  // GET request
  async get<T>(endpoint: string, params?: Record<string, unknown>): Promise<T> {
    let queryString = '';
    if (params) {
      // Filter out undefined, null, and empty string values
      const filteredParams = Object.fromEntries(
        Object.entries(params).filter(([_, value]) => 
          value !== undefined && value !== null && value !== ''
        )
      );
      if (Object.keys(filteredParams).length > 0) {
        queryString = `?${new URLSearchParams(filteredParams as Record<string, string>).toString()}`;
      }
    }
    
    return this.makeRequest(`${endpoint}${queryString}`, {
      method: 'GET',
    });
  }

  // POST request
  async post<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.makeRequest(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  // PUT request
  async put<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.makeRequest(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  // DELETE request
  async delete(endpoint: string): Promise<void> {
    return this.makeRequest(endpoint, {
      method: 'DELETE',
    });
  }
}

// Token management functions
function getAccessToken(): string | null {
  return localStorage.getItem("access_token");
}

function getRefreshToken(): string | null {
  return localStorage.getItem("refresh_token");
}

function setTokens(tokens: { access: string; refresh: string }): void {
  localStorage.setItem("access_token", tokens.access);
  localStorage.setItem("refresh_token", tokens.refresh);
}

function onLogout(): void {
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
  localStorage.removeItem("user_info");
  localStorage.removeItem("permissions");
  localStorage.removeItem("username");
  localStorage.removeItem("clientCode");
}

// Create and export the fetchWrapper instance
export const api = new FetchWrapper({
  getAccessToken,
  getRefreshToken,
  setTokens,
  onLogout,
  refreshEndpoint: 'oauth/token-refresh',
});

// Export the class for testing or custom instances
export { FetchWrapper }; 